import React from 'react'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'

const ContentContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  padding: spacing['16px'],
}))

const TitleContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['16px'],
}))

const SubTitleContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['4px'],
}))

const RangeTitleContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['40px'],
  marginBottom: spacing['4px'],
}))

const RangeItemContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['16px'],
}))

export const PHQPopoverContent = () => {
  const { colors } = useTheme() as ThemeType
  return (
    <ContentContainer>
      <TitleContainer>
        <Title text='PHQ9 - Life Impairment Question' size={TitleSize.MEDIUM} />
      </TitleContainer>
      <SubTitleContainer>
        <Title text='Question' size={TitleSize.SMALL} color={colors.textPopoverSubtitle} />
      </SubTitleContainer>

      <Title
        text='How difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?'
        size={TitleSize.SMALL}
        color={colors.textPrimary}
      />

      <RangeTitleContainer>
        <Title text='Range' size={TitleSize.SMALL} color={colors.textPopoverSubtitle} />
      </RangeTitleContainer>

      <Title text='Not difficult at all' size={TitleSize.SMALL} color={colors.textPrimary} />
      <RangeItemContainer>
        <Title text='Somewhat difficult' size={TitleSize.SMALL} color={colors.textPrimary} />
      </RangeItemContainer>
      <RangeItemContainer>
        <Title text='Very difficult' size={TitleSize.SMALL} color={colors.textPrimary} />
      </RangeItemContainer>
      <RangeItemContainer>
        <Title text='Extremely difficult' size={TitleSize.SMALL} color={colors.textPrimary} />
      </RangeItemContainer>
    </ContentContainer>
  )
}

export const AlliancePopoverContent = ({
  programName,
  providerType,
}: {
  programName: string
  providerType: string
}) => {
  const { colors } = useTheme() as ThemeType
  return (
    <ContentContainer>
      <TitleContainer>
        <Title text='Alliance Questions' size={TitleSize.MEDIUM} />
      </TitleContainer>
      <SubTitleContainer>
        <Title text='Questions' size={TitleSize.SMALL} color={colors.textPopoverSubtitle} />
      </SubTitleContainer>

      <Title
        text={`How satisfied are you with the ${programName.toLowerCase()} approach in terms of it meeting your needs?`}
        size={TitleSize.SMALL}
        color={colors.textPrimary}
      />
      <RangeItemContainer>
        <Title
          text={`How satisfied are you with your relationship (and emotional bond) with your ${providerType}?`}
          size={TitleSize.SMALL}
          color={colors.textPrimary}
        />
      </RangeItemContainer>
      <RangeItemContainer>
        <Title
          text='How satisfied are you with what you worked on or talked about?'
          size={TitleSize.SMALL}
          color={colors.textPrimary}
        />
      </RangeItemContainer>

      <RangeTitleContainer>
        <Title text='Range' size={TitleSize.SMALL} color={colors.textPopoverSubtitle} />
      </RangeTitleContainer>

      <Title text='Unsatisfied' size={TitleSize.SMALL} color={colors.textPrimary} />
      <RangeItemContainer>
        <Title text='Somewhat satisfied' size={TitleSize.SMALL} color={colors.textPrimary} />
      </RangeItemContainer>
      <RangeItemContainer>
        <Title text='Satisfied' size={TitleSize.SMALL} color={colors.textPrimary} />
      </RangeItemContainer>
      <RangeItemContainer>
        <Title text='Very satisfied' size={TitleSize.SMALL} color={colors.textPrimary} />
      </RangeItemContainer>
    </ContentContainer>
  )
}
export const SelfHarmPopoverContent = () => {
  const { colors } = useTheme() as ThemeType

  return (
    <ContentContainer>
      <TitleContainer>
        <Title text='PHQ9 Thoughts of Self Harm Question' size={TitleSize.MEDIUM} />
      </TitleContainer>

      <SubTitleContainer>
        <Title text='Question' size={TitleSize.SMALL} color={colors.textPopoverSubtitle} />
      </SubTitleContainer>
      <Title
        text='Thoughts that you would be better off dead or of hurting yourself in some way.'
        size={TitleSize.SMALL}
        color={colors.textPrimary}
      />

      <RangeTitleContainer>
        <Title text='Range' size={TitleSize.SMALL} color={colors.textPopoverSubtitle} />
      </RangeTitleContainer>

      <Title text='Not at all' size={TitleSize.SMALL} color={colors.textPrimary} />
      <RangeItemContainer>
        <Title text='Several days' size={TitleSize.SMALL} color={colors.textPrimary} />
      </RangeItemContainer>
      <RangeItemContainer>
        <Title text='More than half the days' size={TitleSize.SMALL} color={colors.textPrimary} />
      </RangeItemContainer>
      <RangeItemContainer>
        <Title text='Nearly everyday' size={TitleSize.SMALL} color={colors.textPrimary} />
      </RangeItemContainer>
    </ContentContainer>
  )
}

export const LeapsNwImpContent = () => (
  <ContentContainer>
    <BodyText
      text='During the past 2 weeks, how much did health problems affect your ability to do your regular daily activities, other than work at a job?'
      size={BodyTextSize.DEFAULT}
    />
  </ContentContainer>
)

export const LeapsPrdSSContent = () => (
  <ContentContainer>
    <BodyText
      text='Measures impairment on work-specific issues (e.g., getting less work done), with higher scores indicating greater levels of impairment'
      size={BodyTextSize.DEFAULT}
    />
  </ContentContainer>
)
